import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PermissionGate from "../../components/common/PermissionGate";
import {
  CheckCircle,
  Cancel,
  Visibility,
  Close,
  CalendarMonth,
  School,
  People,
  AttachFile,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useAllLeaves, useApproveLeave, useRejectLeave } from "../../hooks/queries/useLeaves";
import Toast from "../../components/common/Toast";
import { useSelector } from "react-redux";
import { usePermissions } from "../../hooks/usePermissions";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY, hh:mm A") : String(value);
};

const TeacherLeaves = () => {
  const { user } = useSelector((state) => state.auth);
  const { hasVisiblePermission } = usePermissions();
  const canApproveLeave = hasVisiblePermission("leave.approve");
  const canRejectLeave = hasVisiblePermission("leave.reject");

  const newUserRole = user?.role;

  // Default to "student" for teachers, "staff" for others
  const [mainTab, setMainTab] = useState(newUserRole === "teacher" ? "student" : "staff"); // "student" or "staff"
  const [activeTab, setActiveTab] = useState(0);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");

  const approveLeaveMutation = useApproveLeave();
  const rejectLeaveMutation = useRejectLeave();

  // Set default tab to "student" for teachers
  useEffect(() => {
    if (newUserRole === "teacher") {
      setMainTab("student");
    }
  }, [newUserRole]);

  // Determine status filter based on activeTab
  const statusFilter = useMemo(() => {
    if (activeTab === 0) return "all";
    if (activeTab === 1) return "pending";
    if (activeTab === 2) return "approved";
    if (activeTab === 3) return "rejected";
    return "all";
  }, [activeTab]);

  // Determine userType filter based on mainTab
  const userTypeFilter = useMemo(() => {
    return mainTab === "student" ? "student" : "staff";
  }, [mainTab]);

  // API query parameters
  const queryParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1, // API uses 1-based pagination
      limit: paginationModel.pageSize,
      status: statusFilter,
      leaveType: "all", // Can be extended later
    };

    return params;
  }, [paginationModel, statusFilter, userTypeFilter]);

  // Fetch leaves from API
  const { data: leavesResponse, isLoading, isError, error, isFetching } = useAllLeaves(queryParams);

  const isLeaveActionPending =
    approveLeaveMutation.isPending || rejectLeaveMutation.isPending;
  const isListRefreshing = isLoading || isFetching || isLeaveActionPending;

  // Reset to first page when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [mainTab, activeTab]);

  const leaveTypeColors = {
    "Sick Leave": "#f44336",
    "Casual Leave": "#2196f3",
    "Earned Leave": "#4caf50",
    "Medical Leave": "#ff9800",
    Personal: "#9c27b0",
    Medical: "#ff9800",
  };

  const studentColumns = [
    {
      field: "student",
      headerName: "Student",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.row.student[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.student}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.rollNo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "startDate",
      headerName: "From",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />
          {params.value}
        </Box>
      ),
    },
    { field: "endDate", headerName: "To", width: 120 },
    { field: "days", headerName: "Days", width: 70 },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "approved"
              ? "success"
              : params.value === "rejected"
                ? "error"
                : "warning"
          }
        />
      ),
    },
    {
      field: "actionedBy",
      headerName: "Approved by / Rejected by",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        const st = params.row.status;
        if (st !== "approved" && st !== "rejected") return "—";
        const { actionedBy } = params.row;
        if (!actionedBy) return "—";
        return (
          actionedBy.fullName ||
          actionedBy.name ||
          (typeof actionedBy === "string" ? actionedBy : "—")
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedLeave({ ...params.row, isStudent: true });
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          {params.row.status === "pending" && (canApproveLeave || canRejectLeave) && (
            <>
              {canApproveLeave ? (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => {
                    setSelectedLeave({ ...params.row, isStudent: true });
                    setRemarks("");
                    setViewDialogOpen(true);
                  }}
                >
                  <CheckCircle />
                </IconButton>
              ) : null}
              {canRejectLeave ? (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedLeave({ ...params.row, isStudent: true });
                    setRemarks("");
                    setViewDialogOpen(true);
                  }}
                >
                  <Cancel />
                </IconButton>
              ) : null}
            </>
          )}
        </Box>
      ),
    },
  ];

  // Staff columns
  const staffColumns = [
    {
      field: "name",
      headerName: "Staff Member",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
            {params.row.name[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.designation} • {params.row.empId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: (leaveTypeColors[params.value] || "#607d8b") + "20",
            color: leaveTypeColors[params.value] || "#607d8b",
            fontWeight: 500,
          }}
        />
      ),
    },
    {
      field: "startDate",
      headerName: "From",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />
          {params.value}
        </Box>
      ),
    },
    { field: "endDate", headerName: "To", width: 120 },
    {
      field: "days",
      headerName: "Days",
      width: 70,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "attachment",
      headerName: "",
      width: 50,
      renderCell: (params) =>
        params.value ? (
          <IconButton
            size="small"
            color="primary"
            onClick={() => window.open(params.value, '_blank', 'noopener,noreferrer')}
          >
            <AttachFile fontSize="small" />
          </IconButton>
        ) : null,
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "approved"
              ? "success"
              : params.value === "rejected"
                ? "error"
                : "warning"
          }
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "actionedBy",
      headerName: "Approved by / Rejected by",
      flex: 1,
      minWidth: 190,
      renderCell: (params) => {
        const st = params.row.status;
        if (st !== "approved" && st !== "rejected") return "—";
        const { actionedBy } = params.row;
        if (!actionedBy) return "—";
        return (
          actionedBy.fullName ||
          actionedBy.name ||
          (typeof actionedBy === "string" ? actionedBy : "—")
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedLeave({ ...params.row, isStudent: false });
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          {params.row.status === "pending" && (canApproveLeave || canRejectLeave) && (
            <>
              {canApproveLeave ? (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => {
                    setSelectedLeave({ ...params.row, isStudent: false });
                    setRemarks("");
                    setViewDialogOpen(true);
                  }}
                >
                  <CheckCircle />
                </IconButton>
              ) : null}
              {canRejectLeave ? (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedLeave({ ...params.row, isStudent: false });
                    setRemarks("");
                    setViewDialogOpen(true);
                  }}
                >
                  <Cancel />
                </IconButton>
              ) : null}
            </>
          )}
        </Box>
      ),
    },
  ];

  // Extract and map leaves data from API response
  const leaves = useMemo(() => {
    if (!leavesResponse) {
      console.log('No leavesResponse yet');
      return [];
    }

    let leavesArray = [];

    // Axios wraps the response in .data
    // Full axios response: { data: { status: 200, message: "...", data: { leaves: [...] } }, status: 200, ... }
    // So leavesResponse.data = { status: 200, message: "...", data: { leaves: [...] } }
    // And leavesResponse.data.data.leaves = the actual leaves array

    // First, check if this is the full axios response (has status, statusText, headers, etc.)
    if (leavesResponse.status && leavesResponse.statusText && leavesResponse.data) {
      // This is the full axios response, extract from response.data.data.leaves
      if (leavesResponse.data?.data?.leaves && Array.isArray(leavesResponse.data.data.leaves)) {
        leavesArray = leavesResponse.data.data.leaves;
      } else if (leavesResponse.data?.leaves && Array.isArray(leavesResponse.data.leaves)) {
        // Fallback: maybe the API response structure is different
        leavesArray = leavesResponse.data.leaves;
      }
    }
    // Check for leavesResponse.data.leaves (if hook unwrapped axios response)
    else if (leavesResponse.data?.leaves && Array.isArray(leavesResponse.data.leaves)) {
      // Structure: { status: 200, message: "...", data: { leaves: [...] } }
      leavesArray = leavesResponse.data.leaves;
    } else if (leavesResponse.leaves && Array.isArray(leavesResponse.leaves)) {
      // Structure: { leaves: [...] } (if axios unwrapped)
      leavesArray = leavesResponse.leaves;
    } else if (Array.isArray(leavesResponse)) {
      // Direct array
      leavesArray = leavesResponse;
    } else if (leavesResponse.data && Array.isArray(leavesResponse.data)) {
      // Structure: { data: [...] }
      leavesArray = leavesResponse.data;
    } else if (leavesResponse.rows && Array.isArray(leavesResponse.rows)) {
      leavesArray = leavesResponse.rows;
    } else {
      console.warn('Unknown response structure:', {
        response: leavesResponse,
        keys: leavesResponse ? Object.keys(leavesResponse) : [],
        hasData: !!leavesResponse?.data,
        hasDataLeaves: !!leavesResponse?.data?.leaves,
        dataType: typeof leavesResponse?.data,
        dataKeys: leavesResponse?.data ? Object.keys(leavesResponse.data) : [],
      });
    }

    // Filter by userRole (student vs staff/teacher/hod)
    // Note: If API already filters by userType, we might not need this frontend filter
    // But keeping it as a safety net in case API doesn't filter
    const filteredLeaves = leavesArray.filter((leave) => {
      const userRole = leave.userRole?.toLowerCase();
      if (mainTab === "student") {
        return userRole === "student";
      } else {
        // For staff tab, show teacher, hod, and staff roles
        return userRole === "teacher" || userRole === "hod" || userRole === "staff";
      }
    });

    // If no leaves after filtering, log why
    if (filteredLeaves.length === 0 && leavesArray.length > 0) {
      console.warn('All leaves filtered out!', {
        mainTab,
        expectedRole: mainTab === "student" ? "student" : "teacher/hod/staff",
        actualRoles: [...new Set(leavesArray.map(l => l.userRole))],
        sampleLeaveUserRole: leavesArray[0]?.userRole,
      });
    }

    // Map API response to component format
    const mappedLeaves = filteredLeaves.map((leave) => {
      const user = leave.User || leave.user || {};
      const userName =
        user.fullName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.name ||
        "-";

      // Format leave type (convert snake_case to Title Case)
      const formatLeaveType = (type) => {
        if (!type) return "-";
        return type
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };

      // Normalize status (handle cancelled, pending, approved, rejected)
      const normalizeStatus = (status, isApproved) => {
        if (status === "cancelled") return "cancelled";
        if (status === "approved") return "approved";
        if (status === "rejected") return "rejected";
        if (status === "pending") return "pending";
        // Fallback to isApproved flag
        return isApproved ? "approved" : "pending";
      };

      const normalizedStatus = normalizeStatus(leave.status, leave.isApproved);
      const formattedType = formatLeaveType(
        leave.leaveType ||
        leave.type ||
        leave.LeaveManage?.leaveTypeName ||
        leave.leaveManage?.leaveTypeName ||
        leave.leaveManage?.leaveType ||
        ""
      );

      if (mainTab === "student") {
        return {
          id: leave.id || leave._id,
          student: userName,
          rollNo: user.rollNo || leave.studentId || "-",
          type: formattedType,
          startDate: leave.leaveFrom || leave.startDate || leave.fromDate || "-",
          endDate: leave.leaveTo || leave.endDate || leave.toDate || "-",
          days: leave.duration || leave.days || 0,
          reason: leave.notes || leave.reason || leave.description || "-",
          status: normalizedStatus,
          appliedOn: leave.createdAt || leave.appliedOn,
          attachment: leave.document || leave.attachment || leave.attachmentUrl || null,
          actionedBy: leave.approvedBy,
          actionedOn: normalizedStatus === "approved" ? leave.approvedOn : leave.rejectedOn,
          approvedOn: leave.approvedOn || null,
          rejectedOn: leave.rejectedOn || null,
          remarks: leave.rejectionReason || leave.remarks || null,
        };
      } else {
        return {
          id: leave.id || leave._id,
          name: userName,
          empId: user.empId || leave.teacherId || leave.userId || "-",
          designation:
            user.designation ||
            leave.designation ||
            leave.Teacher?.designation ||
            "-",
          department: user.department || (leave.departmentId ? `Dept ${leave.departmentId}` : "-"),
          type: formattedType,
          startDate: leave.leaveFrom || leave.startDate || leave.fromDate || "-",
          endDate: leave.leaveTo || leave.endDate || leave.toDate || "-",
          days: leave.duration || leave.days || 0,
          reason: leave.notes || leave.reason || leave.description || "-",
          status: normalizedStatus,
          appliedOn: leave.createdAt || leave.appliedOn,
          attachment: leave.document || leave.attachment || leave.attachmentUrl || null,
          remarks: leave.rejectionReason || leave.remarks || null,
          actionedBy: leave.approvedBy,
          actionedOn: normalizedStatus === "approved" ? leave.approvedOn : leave.rejectedOn,
          approvedOn: leave.approvedOn || null,
          rejectedOn: leave.rejectedOn || null,
        };
      }
    });


    return mappedLeaves;
  }, [leavesResponse, mainTab]);

  // Get total count for pagination
  const totalCount = useMemo(() => {
    if (leavesResponse?.data?.pagination?.totalItems) {
      return leavesResponse.data.pagination.totalItems;
    }
    if (leavesResponse?.data?.pagination?.total) {
      return leavesResponse.data.pagination.total;
    }
    if (leavesResponse?.pagination?.totalItems) {
      return leavesResponse.pagination.totalItems;
    }
    if (leavesResponse?.pagination?.total) {
      return leavesResponse.pagination.total;
    }
    if (leavesResponse?.data?.total) {
      return leavesResponse.data.total;
    }
    if (leavesResponse?.total) {
      return leavesResponse.total;
    }
    // If no pagination info, return current filtered leaves length
    return leaves.length;
  }, [leavesResponse, leaves.length]);

  // Calculate stats from current leaves
  const stats = useMemo(() => {
    const pending = leaves.filter((l) => l.status?.toLowerCase() === "pending").length;
    const approved = leaves.filter((l) => l.status?.toLowerCase() === "approved").length;
    const rejected = leaves.filter((l) => l.status?.toLowerCase() === "rejected").length;

    return { pending, approved, rejected };
  }, [leaves]);

  const handleApprove = async () => {
    if (!selectedLeave?.id) return;

    try {
      await approveLeaveMutation.mutateAsync({
        id: selectedLeave.id,
        data: { remarks: remarks.trim() || undefined },
      });
      setToastMessage("Leave approved successfully!");
      setToastSeverity("success");
      setViewDialogOpen(false);
      setRemarks("");
      setSelectedLeave(null);
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve leave. Please try again."
      );
      setToastSeverity("error");
    }
  };

  const handleReject = async () => {
    if (!selectedLeave?.id) return;

    try {
      await rejectLeaveMutation.mutateAsync({
        id: selectedLeave.id,
        data: { remarks: remarks.trim() || undefined },
      });
      setToastMessage("Leave rejected successfully!");
      setToastSeverity("success");
      setViewDialogOpen(false);
      setRemarks("");
      setSelectedLeave(null);
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject leave. Please try again."
      );
      setToastSeverity("error");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Leave Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Approve or reject leave applications from students and reporting
            staff
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={mainTab}
          exclusive
          disabled={isLeaveActionPending}
          onChange={(e, v) => {
            if (v) {
              setMainTab(v);
              setActiveTab(0);
            }
          }}
          sx={{
            display: "flex",
            gap: 1,
            "& .MuiToggleButton-root": {
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            },
            "& .Mui-selected": {
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
            },
          }}
        >
          <ToggleButton value="student">
            <School sx={{ mr: 3 }} /> Student Leaves
          </ToggleButton>
          {newUserRole !== "teacher" && (
            <ToggleButton value="staff">
              <People sx={{ mr: 1 }} /> Staff Leaves
            </ToggleButton>
          )}

        </ToggleButtonGroup>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {mainTab === "student" ? (
          <>
            You are viewing leave requests from <strong>students</strong> in
            your class/department.
          </>
        ) : (
          <>
            You are viewing leave requests from <strong>reporting staff</strong>{" "}
            (teachers/instructors under your supervision).
          </>
        )}
      </Alert>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Pending",
            value: isListRefreshing ? "…" : stats.pending,
            color: "warning.main",
          },
          {
            label: "Approved",
            value: isListRefreshing ? "…" : stats.approved,
            color: "success.main",
          },
          {
            label: "Rejected",
            value: isListRefreshing ? "…" : stats.rejected,
            color: "error.main",
          },
        ].map((stat, idx) => (
          <Grid item xs={4} key={stat.label}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ position: "relative" }}>
        {isListRefreshing && !isLoading && (
          <LinearProgress
            sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2, borderRadius: "4px 4px 0 0" }}
          />
        )}
        <CardContent>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            disabled={isLeaveActionPending}
            sx={{ mb: 2 }}
          >
            <Tab label="All" />
            <Tab label="Pending" />
            <Tab label="Approved" />
            <Tab label="Rejected" />
          </Tabs>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error?.response?.data?.message || error?.message || "Failed to load leaves. Please try again."}
            </Alert>
          )}

          {isLoading && leaves.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={leaves}
              columns={mainTab === "student" ? studentColumns : staffColumns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={totalCount}
              paginationMode="server"
              loading={isListRefreshing}
              sx={{
                border: "none",
                opacity: isLeaveActionPending ? 0.72 : 1,
                pointerEvents: isLeaveActionPending ? "none" : "auto",
                transition: "opacity 0.2s ease",
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* View & Action Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedLeave?.isStudent ? (
                <School color="primary" />
              ) : (
                <People color="primary" />
              )}
              <Typography variant="h6">
                {selectedLeave?.isStudent ? "Student" : "Staff"} Leave Request
              </Typography>
            </Box>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLeave && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 2,
                  }}
                >
                  <Avatar
                    sx={{ width: 50, height: 50, bgcolor: "primary.main" }}
                  >
                    {selectedLeave.isStudent
                      ? selectedLeave.student?.[0]
                      : selectedLeave.name?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedLeave.isStudent
                        ? selectedLeave.student
                        : selectedLeave.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedLeave.isStudent
                        ? `Roll No: ${selectedLeave.rollNo}`
                        : `${selectedLeave.designation} • ${selectedLeave.empId}`}
                    </Typography>
                    {!selectedLeave.isStudent && selectedLeave.department && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedLeave.department}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={selectedLeave.status}
                    size="small"
                    color={
                      selectedLeave.status === "approved"
                        ? "success"
                        : selectedLeave.status === "rejected"
                          ? "error"
                          : "warning"
                    }
                    sx={{ ml: "auto", textTransform: "capitalize" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Leave Type
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedLeave.type}
                    size="small"
                    sx={{
                      bgcolor:
                        (leaveTypeColors[selectedLeave.type] || "#607d8b") +
                        "20",
                      color: leaveTypeColors[selectedLeave.type] || "#607d8b",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedLeave.days} day(s)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  From
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedLeave.startDate}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  To
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {selectedLeave.endDate}
                </Typography>
              </Grid>
              {selectedLeave.appliedOn && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Applied On
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(selectedLeave.appliedOn)}
                  </Typography>
                </Grid>
              )}

              {(selectedLeave.status === "approved" || selectedLeave.status === "rejected") && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
              )}

              {(selectedLeave.status === "approved" || selectedLeave.status === "rejected") && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Approved by / Rejected by
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedLeave.actionedBy?.fullName ||
                        selectedLeave.actionedBy?.name ||
                        selectedLeave.actionedBy ||
                        "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {selectedLeave.status === "approved" ? "Approved On" : "Rejected On"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDateTime(selectedLeave.actionedOn)}
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body2">{selectedLeave.reason}</Typography>
              </Grid>
              {selectedLeave.attachment && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Document
                  </Typography>
                  <Typography
                    component="a"
                    href={selectedLeave.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    Document
                  </Typography>
                </Grid>
              )}
              {selectedLeave.remarks && selectedLeave.status === "rejected" && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>Rejection Reason:</strong> {selectedLeave.remarks}
                    </Typography>
                  </Alert>
                </Grid>
              )}
              {selectedLeave.status === "pending" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks (Optional)"
                    multiline
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add any comments or reasons for your decision..."
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedLeave?.status === "pending" && (
            <>
              <PermissionGate permission="leave.reject">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleReject}
                  disabled={rejectLeaveMutation.isPending}
                  startIcon={rejectLeaveMutation.isPending ? <CircularProgress size={16} /> : <Cancel />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {rejectLeaveMutation.isPending ? "Rejecting..." : "Reject"}
                </Button>
              </PermissionGate>
              <PermissionGate permission="leave.approve">
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleApprove}
                  disabled={approveLeaveMutation.isPending}
                  startIcon={approveLeaveMutation.isPending ? <CircularProgress size={16} /> : <CheckCircle />}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {approveLeaveMutation.isPending ? "Approving..." : "Approve"}
                </Button>
              </PermissionGate>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default TeacherLeaves;
